import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, NavLink, Routes, Route, Navigate } from "react-router-dom";
import { adminApi, mediaUrl, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useSite } from "../contexts/SiteContext";
import { toast } from "sonner";
import {
  LayoutDashboard, Settings, Compass, Box, DollarSign, GraduationCap,
  Image as ImageIcon, Users, CalendarDays, Bot, LogOut, Plus, Trash2, Edit3,
  Loader2, Search, Save, X, Check, Upload, ExternalLink, Eye, EyeOff,
} from "lucide-react";

const SECTIONS = [
  { to: "", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "settings", label: "Site Settings", icon: Settings },
  { to: "tabs", label: "Navigation", icon: Compass },
  { to: "products", label: "Products", icon: Box },
  { to: "pricing", label: "Pricing", icon: DollarSign },
  { to: "courses", label: "Courses", icon: GraduationCap },
  { to: "media", label: "Media", icon: ImageIcon },
  { to: "leads", label: "Leads", icon: Users },
  { to: "consultations", label: "Consultations", icon: CalendarDays },
  { to: "agent-turns", label: "Agent Chats", icon: Bot },
];

export default function AdminDashboard() {
  const { signOut, user } = useAuth();
  const { settings } = useSite();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex" data-testid="admin-shell">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 glass border-r border-white/10 sticky top-0 h-screen overflow-y-auto p-5">
        <div className="mb-8">
          {settings?.logo_url ? (
            <img
              src={mediaUrl(settings.logo_url)}
              alt={settings.brand_name || "Reness AI"}
              className="h-10 w-auto object-contain mb-3"
            />
          ) : (
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#00F0FF] font-bold">
              {settings?.brand_name || "Reness AI"}
            </p>
          )}
          <h2 className="font-display text-xl font-black text-white mt-1">Admin Console</h2>
        </div>

        <nav className="space-y-1">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            return (
              <NavLink
                key={s.to || "root"}
                to={s.to}
                end={s.end}
                data-testid={`admin-nav-${s.label.replace(/\s/g, "-").toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition ${
                    isActive
                      ? "bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30"
                      : "text-zinc-300 hover:bg-white/5 hover:text-white border border-transparent"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                {s.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="mt-10 p-4 rounded-2xl glass">
          <p className="text-xs text-zinc-400">Signed in as</p>
          <p className="text-sm text-white font-semibold truncate">{user?.email}</p>
          <button
            data-testid="admin-logout-btn"
            onClick={async () => {
              await signOut();
              toast.success("Signed out.");
              navigate("/admin/login");
            }}
            className="mt-3 w-full flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 text-zinc-200 text-sm py-2 rounded-lg border border-white/10"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 p-6 md:p-10 max-w-[1400px]">
        <Routes>
          <Route index element={<DashboardHome />} />
          <Route path="/" element={<DashboardHome />} />
          <Route path="settings" element={<SettingsPanel />} />
          <Route path="tabs" element={<TabsPanel />} />
          <Route path="products" element={<ProductsPanel />} />
          <Route path="pricing" element={<PricingPanel />} />
          <Route path="courses" element={<CoursesPanel />} />
          <Route path="media" element={<MediaPanel />} />
          <Route path="leads" element={<LeadsPanel />} />
          <Route path="consultations" element={<ConsultationsPanel />} />
          <Route path="agent-turns" element={<AgentTurnsPanel />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </main>
    </div>
  );
}

// -------- Reusable bits --------
const PageHeader = ({ title, subtitle, action }) => (
  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-8">
    <div>
      <h1 className="font-display text-3xl md:text-4xl font-black text-white tracking-tight">
        {title}
      </h1>
      {subtitle && <p className="text-zinc-400 mt-1 text-sm">{subtitle}</p>}
    </div>
    {action}
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-400 font-bold">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);

const Input = (props) => (
  <input
    {...props}
    className={`w-full bg-black/60 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none transition ${props.className || ""}`}
  />
);

const Textarea = (props) => (
  <textarea
    {...props}
    className={`w-full bg-black/60 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none transition resize-none ${props.className || ""}`}
  />
);

const Btn = ({ variant = "primary", className = "", ...rest }) => {
  const styles = {
    primary: "bg-[#00F0FF] hover:bg-[#33F3FF] text-black",
    danger: "bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/30",
    ghost: "bg-white/5 hover:bg-white/10 text-zinc-200 border border-white/10",
    accent: "bg-[#BC13FE]/15 hover:bg-[#BC13FE]/25 text-[#E18CFF] border border-[#BC13FE]/40",
  };
  return (
    <button
      {...rest}
      className={`px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-2 transition disabled:opacity-50 ${styles[variant]} ${className}`}
    />
  );
};

// -------- Dashboard --------
function DashboardHome() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    adminApi.stats().then(setStats).catch((e) => setError(formatApiError(e)));
  }, []);

  const tiles = [
    { key: "leads", label: "Leads (total)", color: "#00F0FF" },
    { key: "leads_last_7d", label: "Leads · last 7d", color: "#00F0FF" },
    { key: "consultations", label: "Consultations", color: "#BC13FE" },
    { key: "agent_chats", label: "Agent chats", color: "#BC13FE" },
    { key: "products", label: "Products", color: "#FFD60A" },
    { key: "pricing_plans", label: "Pricing plans", color: "#FFD60A" },
    { key: "courses", label: "Courses", color: "#FFD60A" },
    { key: "media", label: "Media files", color: "#FFD60A" },
  ];

  return (
    <>
      <PageHeader title="Dashboard" subtitle="Real-time site & funnel metrics." />
      {error && <p className="text-red-400 mb-4 text-sm">{error}</p>}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {tiles.map((t) => (
          <div key={t.key} className="glass rounded-2xl p-5" data-testid={`admin-stat-${t.key}`}>
            <p className="text-[11px] uppercase tracking-[0.2em] text-zinc-400 font-bold">
              {t.label}
            </p>
            <p
              className="font-display text-4xl font-black mt-2"
              style={{ color: t.color }}
            >
              {stats ? stats[t.key] ?? 0 : "…"}
            </p>
          </div>
        ))}
      </div>
    </>
  );
}

// -------- Settings --------
function SettingsPanel() {
  const { refresh } = useSite();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.getSettings().then(setData);
  }, []);

  if (!data) return <Loader />;

  const upd = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const updNested = (k, sub, v) =>
    setData((d) => ({ ...d, [k]: { ...d[k], [sub]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const out = await adminApi.updateSettings({
        brand_name: data.brand_name,
        logo_url: data.logo_url,
        tagline: data.tagline,
        contact: data.contact,
        social: data.social,
        hero: data.hero,
        footer_text: data.footer_text,
        gemini_api_key: data.gemini_api_key,
      });
      setData(out);
      await refresh();
      toast.success("Saved.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Site Settings"
        subtitle="Brand, hero copy, contact + social — live across the site."
        action={
          <Btn data-testid="settings-save-btn" onClick={save} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </Btn>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass rounded-2xl p-6 space-y-4">
          <h3 className="font-display text-lg font-bold text-white">Brand</h3>
          <Field label="Brand name">
            <Input value={data.brand_name} onChange={(e) => upd("brand_name", e.target.value)} />
          </Field>
          <Field label="Logo URL">
            <Input value={data.logo_url} onChange={(e) => upd("logo_url", e.target.value)} />
          </Field>
          {data.logo_url && (
            <div
              className="mt-2 inline-block rounded-xl p-3 ring-1 ring-white/10"
              style={{
                backgroundImage:
                  "linear-gradient(45deg, rgba(255,255,255,0.06) 25%, transparent 25%), linear-gradient(-45deg, rgba(255,255,255,0.06) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, rgba(255,255,255,0.06) 75%), linear-gradient(-45deg, transparent 75%, rgba(255,255,255,0.06) 75%)",
                backgroundSize: "12px 12px",
                backgroundPosition: "0 0, 0 6px, 6px -6px, -6px 0px",
                backgroundColor: "#1a1a22",
              }}
            >
              <img src={mediaUrl(data.logo_url)} alt="logo" className="h-12 object-contain" />
            </div>
          )}
          <Field label="Tagline">
            <Textarea rows={2} value={data.tagline} onChange={(e) => upd("tagline", e.target.value)} />
          </Field>
          <Field label="Footer text">
            <Input value={data.footer_text} onChange={(e) => upd("footer_text", e.target.value)} />
          </Field>
        </div>

        <div className="glass rounded-2xl p-6 space-y-4">
          <h3 className="font-display text-lg font-bold text-[#00F0FF] flex items-center gap-2">
            <Bot className="w-5 h-5" /> AI Agent Config
          </h3>
          <Field label="Gemini API Key">
            <Input
              type="password"
              placeholder="Paste Google Gemini Key (starts with AIzaSy...)"
              value={data.gemini_api_key || ""}
              onChange={(e) => upd("gemini_api_key", e.target.value)}
            />
          </Field>
          <p className="text-[10px] text-zinc-500 leading-normal">
            Safely stored in the database. Leave blank to fall back to the environment variable.
          </p>
        </div>

        <div className="glass rounded-2xl p-6 space-y-4">
          <h3 className="font-display text-lg font-bold text-white">Hero copy</h3>
          <Field label="Eyebrow">
            <Input value={data.hero.eyebrow} onChange={(e) => updNested("hero", "eyebrow", e.target.value)} />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Line 1">
              <Input value={data.hero.headline_top} onChange={(e) => updNested("hero", "headline_top", e.target.value)} />
            </Field>
            <Field label="Line 2">
              <Input value={data.hero.headline_mid} onChange={(e) => updNested("hero", "headline_mid", e.target.value)} />
            </Field>
            <Field label="Accent">
              <Input value={data.hero.headline_accent} onChange={(e) => updNested("hero", "headline_accent", e.target.value)} />
            </Field>
          </div>
          <Field label="Subline">
            <Textarea rows={3} value={data.hero.subline} onChange={(e) => updNested("hero", "subline", e.target.value)} />
          </Field>
        </div>

        <div className="glass rounded-2xl p-6 space-y-4">
          <h3 className="font-display text-lg font-bold text-white">Contact</h3>
          <Field label="Email"><Input value={data.contact.email} onChange={(e) => updNested("contact", "email", e.target.value)} /></Field>
          <Field label="Phone"><Input value={data.contact.phone} onChange={(e) => updNested("contact", "phone", e.target.value)} /></Field>
          <Field label="Address"><Input value={data.contact.address} onChange={(e) => updNested("contact", "address", e.target.value)} /></Field>
        </div>

        <div className="glass rounded-2xl p-6 space-y-4">
          <h3 className="font-display text-lg font-bold text-white">Social</h3>
          <Field label="Twitter"><Input value={data.social.twitter} onChange={(e) => updNested("social", "twitter", e.target.value)} /></Field>
          <Field label="GitHub"><Input value={data.social.github} onChange={(e) => updNested("social", "github", e.target.value)} /></Field>
          <Field label="LinkedIn"><Input value={data.social.linkedin} onChange={(e) => updNested("social", "linkedin", e.target.value)} /></Field>
          <Field label="Facebook"><Input value={data.social.facebook} onChange={(e) => updNested("social", "facebook", e.target.value)} /></Field>
          <Field label="Instagram"><Input value={data.social.instagram} onChange={(e) => updNested("social", "instagram", e.target.value)} /></Field>
        </div>
      </div>
    </>
  );
}

// -------- Tabs --------
function TabsPanel() {
  const { refresh } = useSite();
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ label: "", path: "/", order: 99, enabled: true });

  const reload = useCallback(async () => {
    setItems(await adminApi.listTabs());
    refresh();
  }, [refresh]);
  useEffect(() => { reload(); }, [reload]);

  const create = async () => {
    if (!draft.label || !draft.path) return toast.error("Label + path required.");
    await adminApi.createTab(draft);
    setDraft({ label: "", path: "/", order: items.length + 1, enabled: true });
    await reload();
    toast.success("Tab added.");
  };
  const updateRow = async (it) => {
    await adminApi.updateTab(it.id, { label: it.label, path: it.path, order: it.order, enabled: it.enabled });
    await reload();
  };
  const remove = async (id) => {
    if (!confirm("Delete this tab?")) return;
    await adminApi.deleteTab(id);
    await reload();
  };

  return (
    <>
      <PageHeader title="Navigation Tabs" subtitle="Add, rename, reorder, hide." />
      <div className="glass rounded-2xl p-6 mb-6 grid grid-cols-1 md:grid-cols-5 gap-3">
        <Input placeholder="Label" value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} data-testid="tab-new-label" />
        <Input placeholder="/path" value={draft.path} onChange={(e) => setDraft({ ...draft, path: e.target.value })} data-testid="tab-new-path" />
        <Input type="number" placeholder="Order" value={draft.order} onChange={(e) => setDraft({ ...draft, order: Number(e.target.value) })} />
        <label className="flex items-center gap-2 text-sm text-zinc-300">
          <input type="checkbox" checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} />
          Enabled
        </label>
        <Btn onClick={create} data-testid="tab-add-btn"><Plus className="w-4 h-4" /> Add tab</Btn>
      </div>

      <div className="glass rounded-2xl divide-y divide-white/5 overflow-hidden">
        {items.map((it) => (
          <div key={it.id} className="grid grid-cols-1 md:grid-cols-5 gap-3 p-4 items-center">
            <Input value={it.label} onChange={(e) => setItems((arr) => arr.map((x) => x.id === it.id ? { ...x, label: e.target.value } : x))} />
            <Input value={it.path} onChange={(e) => setItems((arr) => arr.map((x) => x.id === it.id ? { ...x, path: e.target.value } : x))} />
            <Input type="number" value={it.order} onChange={(e) => setItems((arr) => arr.map((x) => x.id === it.id ? { ...x, order: Number(e.target.value) } : x))} />
            <label className="flex items-center gap-2 text-sm text-zinc-300">
              <input type="checkbox" checked={it.enabled} onChange={(e) => setItems((arr) => arr.map((x) => x.id === it.id ? { ...x, enabled: e.target.checked } : x))} />
              Enabled
            </label>
            <div className="flex gap-2 justify-end">
              <Btn variant="ghost" onClick={() => updateRow(it)}><Save className="w-4 h-4" /> Save</Btn>
              <Btn variant="danger" onClick={() => remove(it.id)}><Trash2 className="w-4 h-4" /></Btn>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="p-6 text-zinc-500 text-sm">No tabs yet.</p>}
      </div>
    </>
  );
}

// -------- Generic CRUD list --------
function GenericCrudPanel({ title, subtitle, listFn, createFn, updateFn, deleteFn, fields, defaults }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | object
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try { setItems(await listFn()); } finally { setLoading(false); }
  }, [listFn]);
  useEffect(() => { reload(); }, [reload]);

  const save = async () => {
    try {
      if (editing.id) await updateFn(editing.id, prepare(editing, fields));
      else await createFn(prepare(editing, fields));
      setEditing(null);
      await reload();
      toast.success("Saved.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete?")) return;
    try { await deleteFn(id); await reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      <PageHeader
        title={title}
        subtitle={subtitle}
        action={
          <Btn onClick={() => setEditing({ ...defaults })} data-testid="crud-new-btn">
            <Plus className="w-4 h-4" /> New
          </Btn>
        }
      />

      {loading ? <Loader /> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map((it) => (
            <div key={it.id} className="glass rounded-2xl p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs text-[#00F0FF] tracking-[0.2em] uppercase font-bold">
                    #{it.order ?? 0} · {it.enabled ? "live" : "hidden"}
                  </p>
                  <h3 className="font-display text-xl font-bold text-white mt-1 truncate">
                    {it.title || it.name}
                  </h3>
                  <p className="text-zinc-400 text-sm mt-1">
                    {it.tagline || it.blurb || it.summary || it.level || ""}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Btn variant="ghost" onClick={() => setEditing(it)}><Edit3 className="w-4 h-4" /></Btn>
                  <Btn variant="danger" onClick={() => remove(it.id)}><Trash2 className="w-4 h-4" /></Btn>
                </div>
              </div>
            </div>
          ))}
          {items.length === 0 && <p className="text-zinc-500 text-sm">Nothing here yet — click "New".</p>}
        </div>
      )}

      {editing && (
        <CrudModal
          title={editing.id ? "Edit" : "Create"}
          fields={fields}
          value={editing}
          onChange={setEditing}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </>
  );
}

const prepare = (val, fields) => {
  const out = {};
  for (const f of fields) {
    if (f.type === "list") {
      if (Array.isArray(val[f.key])) {
        out[f.key] = val[f.key];
      } else {
        out[f.key] = (val[f.key] || "").toString().split("\n").map((s) => s.trim()).filter(Boolean);
      }
    }
    else if (f.type === "number") out[f.key] = Number(val[f.key] ?? 0);
    else if (f.type === "boolean") out[f.key] = !!val[f.key];
    else out[f.key] = val[f.key] ?? "";
  }
  return out;
};

function CrudModal({ title, fields, value, onChange, onClose, onSave }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={onClose} />
      <div className="relative w-full max-w-2xl glass-strong rounded-3xl p-7 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-2xl font-black text-white">{title}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          {fields.map((f) => (
            <Field key={f.key} label={f.label}>
              {f.type === "textarea" ? (
                <Textarea rows={3} value={value[f.key] ?? ""} onChange={(e) => onChange({ ...value, [f.key]: e.target.value })} />
              ) : f.type === "list" ? (
                <Textarea
                  rows={4}
                  placeholder="One item per line"
                  value={Array.isArray(value[f.key]) ? value[f.key].join("\n") : value[f.key] ?? ""}
                  onChange={(e) => onChange({ ...value, [f.key]: e.target.value })}
                />
              ) : f.type === "boolean" ? (
                <label className="flex items-center gap-2 text-sm text-zinc-300">
                  <input type="checkbox" checked={!!value[f.key]} onChange={(e) => onChange({ ...value, [f.key]: e.target.checked })} />
                  {f.label}
                </label>
              ) : f.type === "number" ? (
                <Input type="number" value={value[f.key] ?? 0} onChange={(e) => onChange({ ...value, [f.key]: Number(e.target.value) })} />
              ) : (
                <Input value={value[f.key] ?? ""} onChange={(e) => onChange({ ...value, [f.key]: e.target.value })} />
              )}
            </Field>
          ))}
        </div>
        <div className="mt-6 flex gap-2 justify-end">
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={onSave} data-testid="crud-save-btn"><Save className="w-4 h-4" /> Save</Btn>
        </div>
      </div>
    </div>
  );
}

const Loader = () => (
  <div className="text-zinc-400 flex items-center gap-2 text-sm">
    <Loader2 className="w-4 h-4 animate-spin" /> Loading…
  </div>
);

// -------- Products / Pricing / Courses --------
function ProductsPanel() {
  return (
    <GenericCrudPanel
      title="Products" subtitle="Showcased on the Product page."
      listFn={adminApi.listProducts} createFn={adminApi.createProduct}
      updateFn={adminApi.updateProduct} deleteFn={adminApi.deleteProduct}
      defaults={{ title: "", tagline: "", features: [], icon: "Layers", image_url: "", order: 99, enabled: true }}
      fields={[
        { key: "title", label: "Title" },
        { key: "tagline", label: "Tagline", type: "textarea" },
        { key: "features", label: "Features (one per line)", type: "list" },
        { key: "icon", label: "Icon name (lucide)" },
        { key: "image_url", label: "Image URL" },
        { key: "order", label: "Order", type: "number" },
        { key: "enabled", label: "Enabled", type: "boolean" },
      ]}
    />
  );
}

function PricingPanel() {
  return (
    <GenericCrudPanel
      title="Pricing" subtitle="Plans on the Pricing page."
      listFn={adminApi.listPricing} createFn={adminApi.createPricing}
      updateFn={adminApi.updatePricing} deleteFn={adminApi.deletePricing}
      defaults={{ name: "", price: "", cadence: "/month", blurb: "", features: [], highlight: false, order: 99, enabled: true }}
      fields={[
        { key: "name", label: "Plan name" },
        { key: "price", label: "Price (e.g. $999)" },
        { key: "cadence", label: "Cadence (e.g. /month)" },
        { key: "blurb", label: "Short description", type: "textarea" },
        { key: "features", label: "Features (one per line)", type: "list" },
        { key: "highlight", label: "Highlight as 'Most popular'", type: "boolean" },
        { key: "order", label: "Order", type: "number" },
        { key: "enabled", label: "Enabled", type: "boolean" },
      ]}
    />
  );
}

function CoursesPanel() {
  return (
    <GenericCrudPanel
      title="Courses" subtitle="Catalog on the Courses page."
      listFn={adminApi.listCourses} createFn={adminApi.createCourse}
      updateFn={adminApi.updateCourse} deleteFn={adminApi.deleteCourse}
      defaults={{ title: "", level: "Beginner", duration: "", summary: "", image_url: "", order: 99, enabled: true }}
      fields={[
        { key: "title", label: "Title" },
        { key: "level", label: "Level (Beginner/Intermediate/Advanced)" },
        { key: "duration", label: "Duration (e.g. 4 weeks)" },
        { key: "summary", label: "Summary", type: "textarea" },
        { key: "image_url", label: "Image URL" },
        { key: "order", label: "Order", type: "number" },
        { key: "enabled", label: "Enabled", type: "boolean" },
      ]}
    />
  );
}

// -------- Media --------
function MediaPanel() {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const reload = useCallback(async () => setItems(await adminApi.listMedia()), []);
  useEffect(() => { reload(); }, [reload]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await adminApi.uploadMedia(file);
      await reload();
      toast.success("Uploaded.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const copy = async (m) => {
    const url = mediaUrl(m.url);
    try { await navigator.clipboard.writeText(url); toast.success("URL copied."); }
    catch { toast(url); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this file?")) return;
    await adminApi.deleteMedia(id);
    await reload();
  };

  return (
    <>
      <PageHeader
        title="Media Library"
        subtitle="Images & videos (max 50 MB). URLs are reusable across the site."
        action={
          <>
            <input ref={fileRef} type="file" hidden onChange={upload} accept="image/*,video/*" data-testid="media-file-input" />
            <Btn onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="media-upload-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload
            </Btn>
          </>
        }
      />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((m) => (
          <div key={m.id} className="glass rounded-2xl overflow-hidden">
            <div className="aspect-video bg-black/60 flex items-center justify-center overflow-hidden">
              {m.type === "image" ? (
                <img src={mediaUrl(m.url)} alt={m.filename} className="w-full h-full object-cover" />
              ) : (
                <video src={mediaUrl(m.url)} className="w-full h-full object-cover" muted />
              )}
            </div>
            <div className="p-3">
              <p className="text-xs text-zinc-300 truncate">{m.filename}</p>
              <p className="text-[10px] text-zinc-500 mt-0.5">{(m.size / 1024).toFixed(1)} KB · {m.type}</p>
              <div className="mt-2 flex gap-1">
                <Btn variant="ghost" className="!px-2 !py-1.5" onClick={() => copy(m)}><ExternalLink className="w-3.5 h-3.5" /></Btn>
                <Btn variant="danger" className="!px-2 !py-1.5" onClick={() => remove(m.id)}><Trash2 className="w-3.5 h-3.5" /></Btn>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-zinc-500 text-sm">No files yet.</p>}
      </div>
    </>
  );
}

// -------- Leads / Consultations / Agent Turns --------
function ListPanel({ title, subtitle, fetcher, columns, deleteFn, testid }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try { setRows(await fetcher(q)); } finally { setLoading(false); }
  }, [fetcher, q]);

  useEffect(() => { reload(); }, []); // initial

  const onSearch = async (e) => {
    e.preventDefault();
    reload();
  };

  const exportCsv = () => {
    if (!rows.length) return;
    const cols = columns.map((c) => c.key);
    const lines = [cols.join(",")];
    for (const r of rows) {
      lines.push(cols.map((k) => JSON.stringify((r[k] ?? "").toString())).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${title.toLowerCase()}.csv`;
    a.click();
  };

  return (
    <>
      <PageHeader
        title={title} subtitle={subtitle}
        action={
          <div className="flex gap-2">
            <form onSubmit={onSearch} className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-9 w-56" />
              </div>
              <Btn type="submit" variant="ghost">Filter</Btn>
            </form>
            <Btn variant="accent" onClick={exportCsv}>Export CSV</Btn>
          </div>
        }
      />
      <div className="glass rounded-2xl overflow-hidden" data-testid={testid}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-zinc-400 bg-white/5">
                {columns.map((c) => <th key={c.key} className="px-4 py-3 font-bold">{c.label}</th>)}
                {deleteFn && <th className="px-4 py-3"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {loading ? (
                <tr><td colSpan={columns.length + 1} className="px-4 py-6 text-zinc-500"><Loader /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={columns.length + 1} className="px-4 py-6 text-zinc-500">No records.</td></tr>
              ) : rows.map((r, i) => (
                <tr key={r.id || i} className="hover:bg-white/[0.03]">
                  {columns.map((c) => (
                    <td key={c.key} className="px-4 py-3 text-zinc-200 align-top max-w-md">
                      <div className="truncate" title={(r[c.key] ?? "").toString()}>
                        {c.render ? c.render(r) : (r[c.key] ?? "—")}
                      </div>
                    </td>
                  ))}
                  {deleteFn && (
                    <td className="px-4 py-3 text-right">
                      <Btn variant="danger" className="!px-2 !py-1.5" onClick={async () => {
                        if (!confirm("Delete?")) return;
                        await deleteFn(r.id);
                        reload();
                      }}><Trash2 className="w-3.5 h-3.5" /></Btn>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function LeadsPanel() {
  return (
    <ListPanel
      title="Leads" subtitle="From the home modal." testid="admin-leads"
      fetcher={(q) => adminApi.listLeads(q)}
      deleteFn={adminApi.deleteLead}
      columns={[
        { key: "created_at", label: "Created", render: (r) => (r.created_at || "").slice(0, 16).replace("T", " ") },
        { key: "name", label: "Name" },
        { key: "email", label: "Email" },
        { key: "business", label: "Business" },
        { key: "automation_needs", label: "Needs" },
      ]}
    />
  );
}

function ConsultationsPanel() {
  return (
    <ListPanel
      title="Consultations" subtitle="From the Consult page." testid="admin-consultations"
      fetcher={(q) => adminApi.listConsultations(q)}
      deleteFn={adminApi.deleteConsultation}
      columns={[
        { key: "created_at", label: "Created", render: (r) => (r.created_at || "").slice(0, 16).replace("T", " ") },
        { key: "name", label: "Name" },
        { key: "email", label: "Email" },
        { key: "topic", label: "Topic" },
        { key: "preferred_time", label: "Time" },
        { key: "message", label: "Message" },
      ]}
    />
  );
}

function AgentTurnsPanel() {
  return (
    <ListPanel
      title="Agent Chats" subtitle="Last 200 user turns through the AI agent." testid="admin-agent-turns"
      fetcher={() => adminApi.listAgentTurns()}
      columns={[
        { key: "created_at", label: "Time", render: (r) => (r.created_at || "").slice(0, 16).replace("T", " ") },
        { key: "session_id", label: "Session", render: (r) => (r.session_id || "").slice(0, 8) },
        { key: "prompt", label: "Prompt" },
        { key: "answer", label: "Answer", render: (r) => (r.answer || "").slice(0, 120) + ((r.answer || "").length > 120 ? "…" : "") },
      ]}
    />
  );
}
